import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Contact,
  ContactGrouping,
  ContactListHeaderLayoutMode,
  ContactListHeaderSearchOptions,
  ContactSort,
  ContactType,
  CustomContactGroup,
} from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { BrandingField } from '@app/core/models/branding.models';
import { EventName } from '@app/core/models/event-bus.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { BrandingService } from '@app/core/services/branding.service';
import { EventBusService } from '@app/core/services/event-bus.service';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { IdentityProviderService } from '@app/preferences/services/identity-provider.service';
import { ClearableInputComponent } from '@app/shared/components/clearable-input/clearable-input.component';
import { ContactUpdatesComponent } from '@app/shared/components/contact-updates/contact-updates.component';
import { CreateContactGroupComponent } from '@app/shared/components/create-contact-group/create-contact-group.component';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import {
  ContactGroupingLabelPipe,
  ContactSortLabelPipe,
  ContactTypeLabelPipe,
} from '@app/shared/pipes/contact-filter-labels.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest, firstValueFrom } from 'rxjs';

type ContactGroupWithPermissions = CustomContactGroup & { canEdit: boolean };

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatMenuModule,
    MatButtonModule,
    MatDividerModule,
    ContactTypeLabelPipe,
    MatRadioModule,
    MatCheckboxModule,
    MatTooltipModule,
    ContactGroupingLabelPipe,
    ContactSortLabelPipe,
    TranslateModule,
    BrandingColorDirective,
    ClearableInputComponent,
  ],
  selector: 'app-contact-list-header',
  templateUrl: './contact-list-header.component.html',
  styleUrls: ['./contact-list-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactListHeaderComponent implements OnInit {
  @ViewChild('contactListHeaderInput') contactListHeaderInput: ClearableInputComponent;

  @Input() layoutMode: ContactListHeaderLayoutMode = 'full';
  @Input() searchLabel?: string;
  @Input() filteringEnabled: boolean = true;
  @Input() showRecentCallsTab: boolean = false;

  @Output() searchChange = new EventEmitter<string>();
  @Output() contactFiltered = new EventEmitter<ContactListHeaderSearchOptions>();
  @Output() listTabChanged = new EventEmitter<'contacts' | 'recentCalls'>();

  protected search = '';
  protected selectedListTab: 'contacts' | 'recentCalls' = 'contacts';
  protected selectedContactType = ContactType.All;
  protected selectedContactSort = ContactSort.FirstName;
  protected selectedContactGrouping = ContactGrouping.Name;
  protected selectedPBXSites: string[] = [];
  protected selectedContactGroup: CustomContactGroup | null = null;
  protected siteFilterExpanded = false;
  protected searchFocus: boolean = false;
  protected hasMicrosoftProvider = false;
  protected contactGroups: ContactGroupWithPermissions[] = [];

  private pendingContactGroupId: number | null = null;

  protected readonly ContactType = ContactType;
  protected contactTypeValues: ContactType[] = Object.values(ContactType).filter((t) => t !== ContactType.Microsoft);
  protected contactGroupingValues = Object.values(ContactGrouping).filter((v) => v !== ContactGrouping.FavoritesOnly);
  protected contactSortValues = Object.values(ContactSort);
  protected pbxSiteValues: string[] = [];

  protected readonly BrandingField = BrandingField;
  brandingData = this.brandingService.brandingData$;

  protected get isAllSitesSelected(): boolean {
    return this.selectedPBXSites.length === 0;
  }

  protected get selectedSitesText(): string {
    if (this.selectedPBXSites.length === 0) {
      return this.appTranslateService.instant('CONTACTS.ALL_SITES');
    }
    return this.selectedPBXSites.join(', ');
  }

  constructor(
    protected contactService: ContactService,
    private router: Router,
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
    protected configService: AppConfigService,
    private dialog: MatDialog,
    private brandingService: BrandingService,
    private eventBusService: EventBusService,
    private appTranslateService: AppTranslateService,
    private identityProviderService: IdentityProviderService
  ) {}

  ngOnInit() {
    this.searchLabel = this.searchLabel ?? this.appTranslateService.instant('CALLS.NEW_CALL_CONTACT_LIST_SEARCH_LABEL');

    this.eventBusService.on(EventName.DialerInputTabKeydown).subscribe(() => {
      this.contactListHeaderInput.nativeElement.focus();
    });

    this.eventBusService
      .on(EventName.FocusSearch)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.contactListHeaderInput.nativeElement.focus());

    void this.identityProviderService.ensureProvidersLoaded();

    combineLatest([
      this.contactService.contactGroups$,
      this.contactService.currentUser$,
      this.configService.loadedProvision$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([contactGroups]) => {
        this.contactGroups = contactGroups.map((group) => ({
          ...group,
          canEdit: this.contactService.canManageCustomContactGroup(group),
        }));

        if (this.selectedContactGroup) {
          const updatedGroup = this.contactGroups.find((group) => group.id === this.selectedContactGroup!.id);
          if (updatedGroup) {
            this.selectedContactGroup = updatedGroup;
          } else {
            this.selectedContactGroup = null;
            this.emitContactFilteredEventWithCurrentState();
            // An empty array is ambiguous — it can mean the group was genuinely deleted, but it's
            // also what contactGroups$ emits on logout (see ContactService's Logout handler) before
            // any data has loaded for the next user. Only persist the clear when a non-empty,
            // presumably-fresh list confirms the group is truly gone — otherwise we'd silently wipe
            // the saved contactGroupId for a user who simply logged out while viewing a group.
            if (contactGroups.length > 0) {
              void this.updateContactFilter();
            }
          }
        } else {
          this.tryRestorePendingContactGroup();
        }
      });

    combineLatest([
      this.configService.data$,
      this.contactService.data$,
      this.identityProviderService.hasMicrosoftProvider$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([config, , hasMicrosoftProvider]) => {
        this.hasMicrosoftProvider = hasMicrosoftProvider;
        this.contactTypeValues = Object.values(ContactType).filter(
          (t) => t !== ContactType.Microsoft || this.hasMicrosoftProvider
        );

        this.selectedContactType = config?.settings.preferences.contactFilter?.type ?? ContactType.All;
        if (this.selectedContactType === ContactType.Microsoft && !this.hasMicrosoftProvider) {
          this.selectedContactType = ContactType.All;
          void this.updateContactFilter();
        }
        this.pendingContactGroupId = config?.settings.preferences.contactFilter?.contactGroupId ?? null;
        this.tryRestorePendingContactGroup();

        this.selectedContactGrouping = config?.settings.preferences.contactFilter?.grouping ?? ContactGrouping.Name;
        this.selectedContactSort = config?.settings.preferences.contactFilter?.sort ?? ContactSort.FirstName;
        this.selectedPBXSites = config?.settings.preferences.contactFilter?.pbxSites ?? [];

        // Update PBX site values from contact service
        this.pbxSiteValues = this.contactService.allPBXSites;

        this.emitContactFilteredEventWithCurrentState();
      });
  }

  protected async addContact(type = ContactType.Personal) {
    const dialogReference = this.dialog.open(ContactUpdatesComponent, {
      data: {
        contact: null,
        status: 'Add',
        type,
      },
      hasBackdrop: true,
      disableClose: true,
      panelClass: ['edit-contact-dialog'],
    });
    const contact = (await firstValueFrom(dialogReference.afterClosed())) as Contact;
    if (contact) {
      void this.router.navigate(['../' + contact.id], { relativeTo: this.route });
    }
  }

  protected openCreateContactGroupDialog(filterMenuTrigger: MatMenuTrigger) {
    filterMenuTrigger.closeMenu();
    this.dialog.open(CreateContactGroupComponent, {
      hasBackdrop: true,
      disableClose: true,
      autoFocus: 'dialog',
      panelClass: ['create-contact-group-dialog-panel'],
    });
  }

  protected editContactGroup(event: Event, group: CustomContactGroup, filterMenuTrigger: MatMenuTrigger): void {
    event.preventDefault();
    event.stopPropagation();
    filterMenuTrigger.closeMenu();
    this.dialog.open(CreateContactGroupComponent, {
      data: group,
      hasBackdrop: true,
      disableClose: true,
      autoFocus: 'dialog',
      panelClass: ['create-contact-group-dialog-panel'],
    });
  }

  protected handleContactSearchInputChange(event: string) {
    this.searchChange.emit(event);
  }

  protected handleListTabChanged(tab: 'contacts' | 'recentCalls') {
    if (this.selectedListTab === tab) {
      return;
    }
    this.selectedListTab = tab;
    this.listTabChanged.emit(tab);
  }

  protected toggleSiteFilterExpanded(event: Event) {
    event.stopPropagation();
    this.siteFilterExpanded = !this.siteFilterExpanded;
  }

  protected isSiteSelected(site: string): boolean {
    return this.selectedPBXSites.includes(site);
  }

  protected async onAllSitesToggle(event: MouseEvent) {
    event.stopPropagation();

    this.selectedPBXSites = [];
    try {
      await this.updateContactFilter();
    } catch (error) {
      console.error(error);
    }
    this.emitContactFilteredEventWithCurrentState();
  }

  protected async onSiteToggle(site: string, event: MouseEvent) {
    event.stopPropagation();

    const index = this.selectedPBXSites.indexOf(site);
    if (index > -1) {
      // Remove site if already selected - create new array for change detection
      this.selectedPBXSites = this.selectedPBXSites.filter((s) => s !== site);
    } else {
      // Add site to selection - create new array for change detection
      this.selectedPBXSites = [...this.selectedPBXSites, site];
    }
    try {
      await this.updateContactFilter();
    } catch (error) {
      console.error(error);
    }
    this.emitContactFilteredEventWithCurrentState();
  }

  protected async onContactTypeChanged(contactType: ContactType) {
    this.selectedContactType = contactType;
    this.selectedContactGroup = null;
    try {
      await this.updateContactFilter();
    } catch (error) {
      console.error(error);
    }

    this.emitContactFilteredEventWithCurrentState();
    this.googleAnalyticsService.contactsSearched();
  }

  protected groupTooltip(group: CustomContactGroup): string {
    const ownerName = this.contactService.contactById(group.ownerId)?.fullName;
    const parts: string[] = [];
    if (ownerName) {
      parts.push(`${this.appTranslateService.instant('CONTACTS.GROUP_OWNER_LABEL')}: ${ownerName}`);
    }
    if (group.description) {
      parts.push(group.description);
    }
    return parts.join(' • ');
  }

  protected async onContactGroupSelected(group: CustomContactGroup): Promise<void> {
    this.selectedContactGroup = group;
    try {
      await this.updateContactFilter();
    } catch (error) {
      console.error(error);
    }

    this.emitContactFilteredEventWithCurrentState();
    this.googleAnalyticsService.contactsSearched();
  }

  protected async onContactGroupingChanged(contactGrouping: ContactGrouping) {
    this.selectedContactGrouping = contactGrouping;
    try {
      await this.updateContactFilter();
    } catch (error) {
      console.error(error);
    }

    this.emitContactFilteredEventWithCurrentState();
    this.googleAnalyticsService.contactsSearched();
  }

  protected async onContactSortChanged(contactSort: ContactSort) {
    this.selectedContactSort = contactSort;
    try {
      await this.updateContactFilter();
    } catch (error) {
      console.error(error);
    }

    this.emitContactFilteredEventWithCurrentState();
    this.googleAnalyticsService.contactsSearched();
  }

  private async updateContactFilter() {
    await firstValueFrom(
      this.configService.updatePreferences({
        contactFilter: {
          sort: this.selectedContactSort,
          grouping: this.selectedContactGrouping,
          type: this.selectedContactType,
          pbxSites: this.selectedPBXSites,
          contactGroupId: this.selectedContactGroup?.id ?? null,
        },
      })
    );
  }

  private tryRestorePendingContactGroup(): void {
    if (this.pendingContactGroupId === null || this.selectedContactGroup) {
      return;
    }

    const group = this.contactGroups.find((g) => g.id === this.pendingContactGroupId);
    if (group) {
      this.pendingContactGroupId = null;
      this.selectedContactGroup = group;
      this.emitContactFilteredEventWithCurrentState();
    }
  }

  private emitContactFilteredEventWithCurrentState() {
    // selectedContactType tracks the user's radio choice only. While a group is active, the
    // effective type for filtering/persistence purposes is always All — deriving it here (rather
    // than forcing selectedContactType to All on group selection) keeps selectedContactType safe
    // to read back verbatim from persisted preferences without clobbering the active group.
    const effectiveContactType = this.selectedContactGroup ? ContactType.All : this.selectedContactType;
    // Only apply site filtering if Company contact type is selected
    const pbxSites =
      effectiveContactType === ContactType.Company && this.selectedPBXSites.length > 0
        ? this.selectedPBXSites
        : undefined;
    const contactGroupId = this.selectedContactGroup?.id;
    console.log(
      `contact filter: ${JSON.stringify({
        contactType: effectiveContactType,
        contactSort: this.selectedContactSort,
        contactGrouping: this.selectedContactGrouping,
        pbxSites,
        contactGroupId,
      })}`
    );
    this.contactFiltered.emit({
      contactType: effectiveContactType,
      contactSort: this.selectedContactSort,
      contactGrouping: this.selectedContactGrouping,
      pbxSites,
      contactGroupId,
    });
  }

  protected handleKeyUp(event: KeyboardEvent) {
    this.googleAnalyticsService.contactsSearched();
    event.preventDefault();
  }

  protected handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      this.eventBusService.emit(EventName.ContactListHeaderInputTabKeydown);
      event.preventDefault();
    }
  }
}
